import { Button, Flex } from "@aws-amplify/ui-react"
import { I18n } from "aws-amplify/utils"
import { icon } from "leaflet"
import { useContext, useEffect } from "react"
import { FaPenToSquare, FaXmark } from "react-icons/fa6"
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet"
import { Link, useParams } from "react-router-dom"
import { CacheKeys } from "../../CacheKeys"
import { LoadingErrorState } from "../../components"
import config, { isProd } from "../../config"
import {
  UserDataContext,
  UserDataContextHelper,
  UserPreferencesContext,
} from "../../contexts"
import { getLinkToOrg } from "../../LinkUtils"
import Address from "./-components/Address"
import FeedComponentItem from "./-components/FeedComponentItem"
import { imageHash } from "./-components/ListItem"
import RecipeItem from "./-components/RecipeItem"

import "leaflet/dist/leaflet.css"
import "./RecipeManagement.css"

const NUM_ORG_IMAGES = config.images.orgs.numImages
const IMAGE_HASH_SEED = config.images.orgs.imageHashSeed

// The marker icon fix is from https://github.com/PaulLeCam/react-leaflet/issues/808#issuecomment-1605338973
const BslMarkerIcon = icon({
  iconUrl: "/marker-icon.png",
  iconRetinaUrl: "/marker-icon-2x.png",
  shadowUrl: "/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
})

export default function OrgDashboard() {
  const params = useParams<{ orgId: string }>()
  const { setUserPreference } = useContext(UserPreferencesContext)
  const { userData } = useContext(UserDataContext)
  // console.log("🧪 OrgDashboard / rootData:", rootData)

  // Read from the context on every render: the org changes while the page is
  // open, e.g. when a machine is linked. Do not copy it into state.
  const org = UserDataContextHelper.tryFindOrg(params.orgId, userData)
  const orgId = org?.id

  useEffect(() => {
    console.log("> OrgDashboard.useEffect (setUserPreference)")
    if (!orgId) {
      // console.log("❌ OrgDash.useEffect (setUserPreference) org is empty")
      return
    }
    if (!setUserPreference) {
      console.log("❌ setUserPreference is not available.")
      return
    }
    // console.log("OrgDash.useEffect (setUserPreference) setting user preference to ", orgId)
    setUserPreference(CacheKeys.UserPreferences.ActiveOrgId, orgId)
  }, [orgId, setUserPreference])

  if (!userData) {
    return (
      <LoadingErrorState
        isLoading={true}
        loadingMessage={
          isProd ? I18n.get("Lade Daten ...") : "Loading (userData) ..."
        }
        isError={false}
        errorMessage=""
      />
    )
  }

  // No org means the user has none with that id — a wrong or stale orgId.
  if (!org) {
    return (
      <LoadingErrorState
        isLoading={false}
        loadingMessage=""
        isError={true}
        errorMessage={I18n.get("Betrieb nicht gefunden.")}
      />
    )
  }

  const recipes = UserDataContextHelper.findRecipes(org)
  const feedComponents = UserDataContextHelper.findFeedComponents(org)
  const imageIdx = imageHash(IMAGE_HASH_SEED, org.id, NUM_ORG_IMAGES)
  const imageSrc =
    org.name === "Andreas" || org.name === "Haggeney"
      ? `/images/orgs/hof-pixabay-1.jpg`
      : org.name === "Heiner" || org.name === "Große Börding"
        ? `/images/orgs/hof-pixabay-2.jpg`
        : `/images/orgs/hof-pixabay-${imageIdx}.jpg`
  return (
    <>
      <div className="rm-view-page organization">
        <div
          className="hdr-img"
          style={{ backgroundImage: `url('${imageSrc}')` }}
        />
        <Link to="..">
          <Button className="button-close has-icon">
            <span role="img" aria-label="Schließen">
              <FaXmark />
            </span>
          </Button>
        </Link>
        <div className="content-overlay">
          <div className="header">
            <div className="info">
              <h2>{org.name}</h2>
              <div className="basic-info">
                <Address address={org.address} />
              </div>
            </div>
            <Link to={getLinkToOrg(org, true)}>
              <Button className="button-edit has-icon">
                <span role="img" aria-label="Bearbeiten">
                  <FaPenToSquare />
                </span>
              </Button>
            </Link>
          </div>

          <Flex
            justifyContent="space-between"
            className="!flex-col md:!flex-row"
          >
            <div className="w-full md:!w-7/12">
              <div className="font-semibold text-[16px] mt-4">
                Meine Rezepte
              </div>
              <div className="mt-2 grid grid-cols-1 md:!grid-cols-2 gap-8">
                {recipes
                  .sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, {
                      sensitivity: "base",
                      numeric: true,
                    })
                  )
                  .map((item) => (
                    <RecipeItem
                      className="!border-none"
                      key={item.id}
                      org={org}
                      item={item}
                    />
                  ))}
              </div>
            </div>

            <div className="w-full md:!w-5/12">
              <div className="font-semibold text-[16px] mt-4">Karte</div>
              {org.address &&
              typeof org.address !== "string" &&
              org.address.gps ? (
                <div className="mt-2 sds-widget map-widget">
                  <MapContainer
                    center={org.address!.gps}
                    zoom={13}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      icon={BslMarkerIcon}
                      position={{
                        lat: org.address!.gps!.lat,
                        lng: org.address!.gps!.lng,
                      }}
                    >
                      <Popup>Standort des Fahrzeugs</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              ) : (
                <div className="text-[14px]">
                  Ohne Adresse gibt's keine Kartenansicht...
                </div>
              )}
            </div>
          </Flex>
        </div>

        <div className="content-overlay mt-6">
          <div className="font-semibold text-[16px] mt-4">
            Meine Komponenten
          </div>
          <div className="w-full mt-4 grid grid-cols-1 md:!grid-cols-3 gap-8">
            {feedComponents
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                  sensitivity: "base",
                  numeric: true,
                })
              )
              .map((item) => (
                <FeedComponentItem
                  className="!border-none"
                  key={item.id}
                  org={org}
                  item={item}
                />
              ))}
          </div>
        </div>

        {/* <div className="content-overlay mt-6">
        <Collection className="details" type="list" direction="row"
          items={[
            {
              title: "Statistiken und Fahrer",
              content: <>...<br />
                Platzhalter<br />
                ...</>
            },
          ]}>
          {(item, index) => {
            return <Card key={item.title} className="!p-2 !border !border-gray-400 !rounded-[5px]" style={{ width: "50%" }}>
              <div className="font-semibold text-[16px]">{item.title}</div>
              <div className="text-[12px]">
                {item.content}
              </div>
            </Card>
          }}
        </Collection>
      </div> */}
      </div>

      {/* <small>* Die Icons "pen-to-square (light)" benötigen FontAwesome Pro, kostet 49,- oder 99,- pro Jahr.</small> */}
    </>
  )
}
